import { clsx } from "clsx"
import { type CSSProperties, forwardRef, type MouseEvent } from "react"

import type { SX, ThemeKeys } from "../../theme"
import { Icon, type IconName } from "../Icon"
import { Tooltip } from "../Tooltip"
import type { TooltipProps } from "../Tooltip/Tooltip"
import styles from "./HairlineButton.module.css"

type Variant = "centerSolid" | "rightDashed"

export interface HairlineButtonProps {
  icon: IconName
  disabled?: boolean
  dropMode?: boolean
  sx?: SX
  onClick?: (event: MouseEvent) => void
  variant?: Variant
  tooltip?: { content: string; side?: TooltipProps["side"] }
  size?: "small" | "medium"
}

export const HairlineButton = forwardRef<
  HTMLButtonElement,
  HairlineButtonProps
>(function HairlineButton(props, ref) {
  const {
    icon,
    dropMode,
    disabled = false,
    sx,
    variant = "centerSolid",
    tooltip,
    size = "medium",
    ...otherProps
  } = props

  const renderedIcon = (
    <div
      className={clsx(
        styles.icon,
        styles[`icon-mode-${getIconStyle({ dropMode, variant })}`],
      )}
    >
      <Icon name={icon} size={size} color="grey2" />
    </div>
  )

  const style: CSSProperties = sx ?? {}
  style["--HairlineButton-size"] = getSize(size)

  const lineStyle = getLineStyle({ dropMode, variant })
  return (
    <button
      {...otherProps}
      className={clsx(
        styles.hairlineButton,
        dropMode && styles["mode-drop"],
        styles[`icon-${getIconPosition({ variant })}`],
      )}
      style={style}
      disabled={disabled}
      ref={ref}
    >
      <div
        className={clsx(
          styles.line,
          styles.firstLine,
          styles[`line-style-${lineStyle}`],
        )}
      />

      {tooltip ? (
        <Tooltip
          content={tooltip.content}
          side={tooltip.side ?? "right"}
          sideOffset={4}
          visible={!disabled}
          delayDuration={0}
          disableHoverableContent
        >
          {renderedIcon}
        </Tooltip>
      ) : (
        renderedIcon
      )}

      <div
        className={clsx(
          styles.line,
          styles.secondLine,
          styles[`line-style-${lineStyle}`],
        )}
      />
    </button>
  )
})

type VariantSelectionProps = {
  dropMode?: boolean | undefined
  variant: Variant
}

function getIconStyle(options: VariantSelectionProps) {
  const { dropMode, variant } = options
  if (dropMode) return "drop"
  if (variant === "rightDashed") return "hover"
  return "normal"
}

function getIconPosition(options: VariantSelectionProps) {
  const { variant } = options
  if (variant === "rightDashed") return "right"
  return "center"
}

function getLineStyle(options: VariantSelectionProps) {
  const { dropMode, variant } = options
  if (dropMode) return "drop"
  if (variant === "rightDashed") return "dashed"
  return "normal"
}

function getSize(size: "small" | "medium"): `${ThemeKeys<"space">}px` {
  if (size === "small") return `16px`
  return `20px`
}
